/**
 * Product reviews. Fills `sections/product-reviews.liquid` from REVIEWS.io's
 * public widget endpoints (the same two the app's Polaris widget reads, no
 * key, CORS open):
 *
 *   breakdown  /timeline/data   aggregations.ratings.buckets, one per star
 *   list       /product/review  reviews.data, paged, newest first
 *
 * Loads once the section nears the screen. Every value from the API goes in
 * with textContent. If either call fails its part stays hidden.
 */
(function () {
  var API = 'https://api.reviews.io';

  function query(params) {
    return Object.keys(params).map(function (key) {
      return encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
    }).join('&');
  }

  function getJSON(path, params) {
    return fetch(API + path + '?' + query(params), { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('reviews ' + response.status);
        return response.json();
      });
  }

  function init(section) {
    if (section.dataset.reviewsReady === 'true') return;
    section.dataset.reviewsReady = 'true';

    var store = section.dataset.store;
    var sku = section.dataset.sku;
    var perPage = Number(section.dataset.perPage) || 4;
    var fallbackTitle = section.dataset.fallbackTitle || '';
    var breakdown = section.querySelector('[data-reviews-breakdown]');
    var list = section.querySelector('[data-reviews-list]');
    var more = section.querySelector('[data-reviews-more]');
    var template = section.querySelector('[data-reviews-template]');
    var starTemplate = section.querySelector('[data-reviews-star]');
    if (!store || !sku || !list || !template) return;

    var page = 0;
    var lastPage = 1;
    var loading = false;

    function loadBreakdown() {
      getJSON('/timeline/data', { type: 'product_review', store: store, sku: sku, per_page: 1, page: 1 })
        .then(function (data) {
          var buckets = (((data || {}).aggregations || {}).ratings || {}).buckets || [];
          var counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
          var total = 0;
          buckets.forEach(function (bucket) {
            if (counts.hasOwnProperty(bucket.key)) {
              counts[bucket.key] = bucket.doc_count;
              total += bucket.doc_count;
            }
          });
          if (!total || !breakdown) return;
          Object.keys(counts).forEach(function (stars) {
            var row = breakdown.querySelector('[data-reviews-row="' + stars + '"]');
            if (!row) return;
            row.querySelector('[data-reviews-count]').textContent = counts[stars];
            row.querySelector('[data-reviews-share]').style.width = (counts[stars] / total * 100) + '%';
          });
          breakdown.hidden = false;
        })
        .catch(function () { /* leave the breakdown hidden */ });
    }

    function starRow(node, rating) {
      if (!starTemplate) return;
      node.setAttribute('aria-label', rating + ' out of 5');
      for (var i = 1; i <= 5; i++) {
        var star = starTemplate.content.firstElementChild.cloneNode(true);
        if (i > rating) star.classList.add('opacity-25');
        star.setAttribute('aria-hidden', 'true');
        node.appendChild(star);
      }
    }

    function render(review) {
      var item = template.content.firstElementChild.cloneNode(true);
      var reviewer = review.reviewer || {};
      var name = [reviewer.first_name, reviewer.last_name].filter(Boolean).join(' ').trim();
      var title = (review.title || '').trim() || fallbackTitle;

      starRow(item.querySelector('[data-review-stars]'), Number(review.rating) || 0);
      item.querySelector('[data-review-title]').textContent = title;
      item.querySelector('[data-review-text]').textContent = (review.review || '').trim();
      item.querySelector('[data-review-author]').textContent = name;
      item.querySelector('[data-review-date]').textContent = review.timeago || '';
      if (!name || !review.timeago) item.querySelector('[data-review-divider]').remove();
      if (String(reviewer.verified_buyer).toLowerCase() === 'yes') {
        item.querySelector('[data-review-verified]').hidden = false;
      }
      list.appendChild(item);
    }

    function loadPage() {
      if (loading || page >= lastPage) return;
      loading = true;
      if (more) more.disabled = true;
      getJSON('/product/review', { store: store, sku: sku, per_page: perPage, page: page + 1 })
        .then(function (data) {
          var reviews = (data && data.reviews) || {};
          page = reviews.current_page || page + 1;
          lastPage = reviews.last_page || page;
          (reviews.data || []).forEach(render);
          if (more) more.hidden = page >= lastPage;
        })
        .catch(function () {
          if (more) more.hidden = true;
        })
        .then(function () {
          loading = false;
          if (more) more.disabled = false;
        });
    }

    if (more) more.addEventListener('click', loadPage);

    function start() {
      loadBreakdown();
      loadPage();
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) {
          observer.disconnect();
          start();
        }
      }, { rootMargin: '600px 0px' });
      observer.observe(section);
    } else {
      start();
    }
  }

  /*
   * Write a review. The app's own Polaris widget, loaded on the first click
   * into the panel under the button, with its review form opened for the
   * customer and the rest of it hidden (CSS, `.product-reviews__write`). The
   * review is submitted by the widget to REVIEWS.io as on the live site.
   */
  var WIDGET_SRC = 'https://widget.reviews.co.uk/polaris/build.js';
  var widgetScript = null;

  function loadWidgetScript() {
    if (window.ReviewsWidget) return Promise.resolve();
    if (widgetScript) return widgetScript;
    widgetScript = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = WIDGET_SRC;
      script.async = true;
      script.onload = function () { window.ReviewsWidget ? resolve() : reject(new Error('no widget')); };
      script.onerror = reject;
      document.head.appendChild(script);
    });
    widgetScript.catch(function () { widgetScript = null; });
    return widgetScript;
  }

  function widgetStyles(host) {
    var css = getComputedStyle(host);
    var text = css.getPropertyValue('--color-text').trim() || '#000000';
    var button = css.getPropertyValue('--color-button').trim() || '#000000';
    var label = css.getPropertyValue('--color-button-label').trim() || '#FFFFFF';
    return {
      '--base-font-size': '16px',
      '--common-button-font-family': 'Montserrat, system-ui, sans-serif',
      '--common-button-font-size': '14px',
      '--common-button-font-weight': '400',
      '--common-button-letter-spacing': '0.05em',
      '--common-button-text-transform': 'uppercase',
      '--common-button-vertical-padding': '12px',
      '--common-button-horizontal-padding': '16px',
      '--common-button-border-width': '1px',
      '--common-button-border-radius': '0px',
      '--primary-button-bg-color': button,
      '--primary-button-border-color': button,
      '--primary-button-text-color': label,
      '--secondary-button-bg-color': 'transparent',
      '--secondary-button-border-color': text,
      '--secondary-button-text-color': text,
      '--common-star-color': '#E5B53D',
      '--common-star-disabled-color': 'rgba(0,0,0,0.25)',
      '--medium-star-size': '22px',
      '--heading-text-color': text,
      '--heading-text-font-weight': '500',
      '--heading-text-font-family': '"EB Garamond", Georgia, serif',
      '--body-text-color': text,
      '--body-text-font-family': '"EB Garamond", Georgia, serif',
      '--inputfield-text-font-family': '"EB Garamond", Georgia, serif',
      '--input-text-font-size': '16px',
      '--inputfield-text-color': text,
      '--inputfield-border-color': 'rgba(0,0,0,0.2)',
      '--inputfield-background-color': 'transparent',
      '--inputfield-border-width': '1px',
      '--inputfield-border-radius': '0px',
      '--common-border-color': 'rgba(0,0,0,0.15)',
      '--common-border-width': '1px'
    };
  }

  // Resolves with the widget's own Write Review button once it has drawn.
  function waitFor(root, test, timeout) {
    return new Promise(function (resolve, reject) {
      var started = Date.now();
      (function check() {
        var found = test(root);
        if (found) return resolve(found);
        if (Date.now() - started > timeout) return reject(new Error('timeout'));
        setTimeout(check, 150);
      })();
    });
  }

  function findWriteButton(root) {
    return Array.prototype.find.call(root.querySelectorAll('.R-Button'), function (el) {
      return /write review/i.test(el.textContent || '');
    });
  }

  function initWrite(host) {
    if (host.dataset.reviewsWriteReady === 'true') return;
    host.dataset.reviewsWriteReady = 'true';

    var open = host.querySelector('[data-reviews-write-open]');
    var panel = host.querySelector('[data-reviews-write-panel]');
    var mount = host.querySelector('[data-reviews-write-mount]');
    var store = host.dataset.store;
    var sku = host.dataset.sku;
    if (!open || !panel || !mount || !store || !sku) return;

    var started = false;

    function close() {
      panel.hidden = true;
      open.hidden = false;
      open.setAttribute('aria-expanded', 'false');
      open.focus();
    }

    function openForm() {
      var button = findWriteButton(mount);
      var form = mount.querySelector('.ElementsWidget__form');
      if (button && !(form && form.classList.contains('isActive'))) button.click();
    }

    // The widget's Cancel collapses its form; close the panel with it.
    mount.addEventListener('click', function (event) {
      var target = event.target.closest('.R-Button');
      if (target && /^\s*cancel\s*$/i.test(target.textContent || '')) {
        setTimeout(close, 0);
      }
    });

    open.addEventListener('click', function () {
      open.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      open.hidden = true;

      if (started) {
        openForm();
        return;
      }
      started = true;
      host.classList.add('is-loading');

      loadWidgetScript()
        .then(function () {
          new window.ReviewsWidget('#' + mount.id, {
            store: store,
            widget: 'polaris',
            options: {
              types: 'product_review',
              lang: 'en',
              per_page: 1,
              product_review: { sku: sku, hide_if_no_results: false },
              header: {
                enable_summary: false,
                enable_ratings: false,
                enable_attributes: false,
                enable_image_gallery: false,
                enable_percent_recommended: false,
                enable_write_review: true,
                enable_ask_question: false,
                enable_sub_header: false
              },
              filtering: { enable: false }
            },
            styles: widgetStyles(host)
          });
          return waitFor(mount, findWriteButton, 15000);
        })
        .then(function (button) {
          host.classList.remove('is-loading');
          button.click();
          return waitFor(mount, function (root) { return root.querySelector('.ElementsWidget__form textarea'); }, 5000);
        })
        .then(function (field) {
          var first = mount.querySelector('.ElementsWidget__form input[name="rating"]') || field;
          if (first) first.focus({ preventScroll: true });
        })
        .catch(function () {
          // The widget could not be reached: say so rather than leave a blank panel.
          host.classList.remove('is-loading');
          started = false;
          mount.textContent = '';
          var note = document.createElement('p');
          note.className = 'body-base';
          note.textContent = 'The review form could not load. Please try again in a moment.';
          mount.appendChild(note);
        });
    });
  }

  function run(scope) {
    (scope || document).querySelectorAll('[data-product-reviews]').forEach(init);
    (scope || document).querySelectorAll('[data-reviews-write]').forEach(initWrite);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { run(); });
  } else {
    run();
  }
  document.addEventListener('shopify:section:load', function (event) {
    run(event.target);
  });
})();
